<template>
  <component
    :is="tag"
    :class="buttonClasses"
    :type="type"
    :disabled="disabled"
    :aria-disabled="disabled"
    @click="handleClick"
  >
    <slot></slot>
  </component>
</template>

<script setup>
import { computed } from 'vue';

/**
 * BaseButton - Reusable button component
 * Supports primary, secondary, and text variants
 */
const props = defineProps({
  /** Button variant */
  variant: {
    type: String,
    default: 'primary',
    validator: (value) => ['primary', 'secondary', 'text'].includes(value)
  },
  /** HTML tag to render */
  tag: {
    type: String,
    default: 'button'
  },
  /** Button type attribute */
  type: {
    type: String,
    default: 'button'
  },
  /** Disabled state */
  disabled: {
    type: Boolean,
    default: false
  },
  /** Full width button */
  block: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['click']);

const buttonClasses = computed(() => {
  return {
    'btn': true,
    'btn--primary': props.variant === 'primary',
    'btn--secondary': props.variant === 'secondary' || props.variant === 'alt',
    'btn--text': props.variant === 'text',
    'btn--block': props.block,
    'btn--disabled': props.disabled
  };
});

function handleClick(event) {
  if (!props.disabled) {
    emit('click', event);
  }
}
</script>

<style scoped>
.btn {
  border: 1px solid var(--accent);
  background: var(--accent);
  color: #fff;
  padding: 12px 18px;
  border-radius: 14px;
  cursor: pointer;
  font-weight: 600;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: transform 0.15s cubic-bezier(0.34, 1.56, 0.64, 1),
              box-shadow 0.3s ease,
              background 0.2s ease,
              color 0.2s ease;
  box-shadow: var(--shadow);
  min-height: 44px;
  touch-action: manipulation;
  position: relative;
  overflow: hidden;
  border: none;
  font-family: inherit;
  font-size: 16px;
}

.btn::before {
  content: '';
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at center, rgba(255, 255, 255, 0.25) 0%, transparent 70%);
  opacity: 0;
  transition: opacity 0.3s ease, transform 0.3s ease;
  transform: scale(0);
  border-radius: 14px;
}

.btn:hover:not(.btn--disabled) {
  transform: translateY(-2px) scale(1.02);
  box-shadow: 0 16px 40px rgba(247, 126, 45, 0.25), 0 0 0 1px rgba(247, 126, 45, 0.2);
  background: color-mix(in oklab, var(--accent) 90%, var(--fg) 10%);
}

.btn:hover:not(.btn--disabled)::before {
  opacity: 1;
  transform: scale(1);
}

.btn:active:not(.btn--disabled) {
  transform: translateY(0) scale(0.98);
}

.btn:active:not(.btn--disabled)::before {
  opacity: 0;
  transform: scale(1.5);
}

.btn--secondary {
  background: var(--card);
  color: var(--accent);
  border: 1px solid var(--accent);
}

.btn--secondary::before {
  background: radial-gradient(circle at center, rgba(247, 126, 45, 0.15) 0%, transparent 70%);
}

.btn--secondary:hover:not(.btn--disabled) {
  background: var(--accent);
  color: #fff;
  box-shadow: 0 16px 40px rgba(247, 126, 45, 0.2), 0 0 0 1px var(--accent);
}

.btn--text {
  background: transparent;
  border: none;
  box-shadow: none;
  color: var(--accent);
  text-decoration: underline;
  text-underline-offset: 4px;
}

.btn--text:hover:not(.btn--disabled) {
  background: rgba(247, 126, 45, 0.1);
  transform: none;
}

.btn--block {
  width: 100%;
}

.btn--disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none !important;
}

@media (max-width: 768px) {
  .btn {
    min-height: 48px;
  }
}
</style>
